/**
 * chatReducer - pure reducer for AI chat session state.
 *
 * State transitions driven by ServerEvents from the WebSocket.
 * Must remain a pure function: no side effects, no async.
 *
 * See context/ai-chat.md §6 for the run lifecycle diagram.
 */

import type {
  ChatMode,
  ChatTriggerSource,
  RunOrigin,
  ContextRetrievalStep,
  ExplicitContextItem,
  GeminiModel,
  GenericProgressStep,
  ServerEvent,
} from '@protoss/shared';
import {
  GENERATION_RESPONDING_LABEL,
  GENERATION_THINKING_LABEL,
  parseAiStructuredPayload,
} from '@protoss/shared';
import {
  addStep,
  cancelExecution,
  completeAgentStep,
  completeExecution,
  completeResponseStep,
  completeToolStep,
  createAgentStep,
  createContextRetrievalStep,
  createInitialExecutionStatus,
  createPlanStepsFromExecutionPlan,
  createProgressStep,
  createToolExecutionStep,
  failAgentStep,
  failExecution,
  updateStep,
  updateToolProgress,
} from '../../../lib/executionStatusHelpers';
import type {
  AssistantRun,
  ChatSessionSummary,
  ChatState,
  ConversationTurn,
  FormAnswerSummary,
  RunSegment,
  SessionSlice,
  UserTurn,
} from './chatState';
import { initialChatState, initialSessionSlice } from './chatState';

/** Narrow a stored session mode string to ChatMode (else undefined). */
function asChatMode(value: string | null | undefined): ChatMode | undefined {
  return value === 'ask' || value === 'agent' || value === 'auto' ? value : undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal action types (UI-driven triggers separate from WS events)
// ─────────────────────────────────────────────────────────────────────────────

export type ChatActionType =
  | { type: 'CONNECTION_STATUS'; sessionUuid: string; status: ChatState['connectionStatus'] }
  | { type: 'RESET' }
  | { type: 'SERVER_EVENT'; sessionUuid: string; event: ServerEvent }
  | {
      type: 'USER_TURN_SENT';
      runId: string;
      prompt: string;
      context: ChatState['selectedContext'];
      triggerSource?: ChatTriggerSource;
      origin?: RunOrigin;
    }
  | { type: 'SET_CONTEXT'; context: ChatState['selectedContext'] }
  | { type: 'SET_COMPOSER_MODE'; mode: ChatMode }
  | { type: 'HISTORY_STATUS'; sessionUuid: string; status: ChatState['historyStatus'] }
  | { type: 'HISTORY_LOADED'; turns: ConversationTurn[] }
  | { type: 'SESSIONS_STATUS'; status: ChatState['sessionsStatus'] }
  | { type: 'SESSIONS_LOADED'; sessions: ChatSessionSummary[] }
  | { type: 'SESSION_CREATED'; session: ChatSessionSummary; skipClearTurns?: boolean }
  | { type: 'SESSION_TITLE_UPDATED'; sessionUuid: string; title: string }
  | { type: 'SESSION_SWITCH_STARTED'; sessionUuid: string }
  | {
      type: 'SESSION_SWITCHED';
      sessionUuid: string;
      turns: ConversationTurn[];
      hasMore: boolean;
      oldestMessageIndex: number | null;
    }
  | {
      type: 'OLDER_HISTORY_LOADED';
      turns: ConversationTurn[];
      hasMore: boolean;
      oldestMessageIndex: number | null;
    }
  // ── Explicit context actions ─────────────────────────────────────────────
  /**
   * Add one explicit context item (from a project menu interaction).
   * Items are additive - existing items are preserved.
   * Duplicate ids are ignored to prevent double-adding on re-click.
   */
  | { type: 'ADD_EXPLICIT_CONTEXT'; item: ExplicitContextItem }
  /**
   * Remove a single explicit context item by its stable id.
   */
  | { type: 'REMOVE_EXPLICIT_CONTEXT'; itemId: string }
  /**
   * Clear all explicit context items (e.g. on "Clear all" action).
   * Also clears the current draft.
   */
  | { type: 'CLEAR_EXPLICIT_CONTEXT' }
  /**
   * Set the single pending draft item. Replaces any existing draft.
   * Called on every tree-node click / Monaco selection button press.
   */
  | { type: 'SET_DRAFT_EXPLICIT_CONTEXT'; item: ExplicitContextItem }
  /**
   * Promote the current draft into confirmed explicitContextItems.
   * Clears draftExplicitItem afterwards.
   */
  | { type: 'CONFIRM_DRAFT_EXPLICIT_CONTEXT' }
  /**
   * Dismiss the current draft without confirming it.
   */
  | { type: 'CLEAR_DRAFT_EXPLICIT_CONTEXT' }
  /**
   * Optimistically cancel the active run without waiting for server ack.
   * Immediately reverts Stop → Send and marks the run as cancelled in UI.
   */
  | { type: 'CANCEL_RUN_OPTIMISTIC'; runId: string; ts: string }
  /**
   * Update the reconnect attempt counter for a session.
   * Dispatched by useChatSession when scheduling or resetting auto-reconnect.
   */
  | { type: 'RECONNECT_ATTEMPT'; sessionUuid: string; attempt: number }
  /**
   * Optimistically record the user's action on a structured message card.
   * Dispatched immediately on click so the UI updates without waiting for
   * the PATCH response.
   */
  | {
      type: 'SET_ACTION_RESULT';
      runId: string;
      actionResult: 'accepted' | 'dismissed';
      acceptedFieldKeys?: string[];
      /** Card key for multi-card structured payloads - updates cardActionResults instead. */
      cardKey?: string;
    }
  /**
   * Remove the optimistic user turn for a run that was aborted before start_run
   * was sent (e.g. because a required persist step failed before the WS send).
   * Also removes the optimistic AssistantRun if OPTIMISTIC_RUN_PENDING was dispatched.
   */
  | { type: 'USER_TURN_ABORTED'; runId: string }
  /**
   * Optimistically create the AssistantRun placeholder immediately when beforeSend
   * is used - so the agent bubble appears before the async persist gate resolves.
   * The real run_started from the server will update model/mode without duplicating.
   */
  | { type: 'OPTIMISTIC_RUN_PENDING'; runId: string; model: GeminiModel; mode: ChatMode }
  /**
   * [Conversational copilot] The user answered a mid-run prompt (chip click or
   * composer free text). Records the answer on the run's prompt segment, clears
   * pendingPrompt, and flips the lifecycle to 'resuming' so the card doesn't
   * linger until the next server event.
   */
  | {
      type: 'PROMPT_ANSWERED';
      runId: string;
      promptId: string;
      answer: string;
      /** Form prompts: per-tab answer summary, shown per-section in the transcript. */
      formAnswers?: FormAnswerSummary[];
      /** For a file answer - the first uploaded asset uuid (kept for back-compat). */
      assetUuid?: string;
      /** For a file answer - all uploaded asset uuids, shown as a thumbnail/name per file. */
      assetUuids?: string[];
      /** Selected choice option(s) with a `preview` - rendered as collapsed disclosures. */
      answerPreviews?: Array<{ label: string; preview: string }>;
      /**
       * [HITL navigation] Set when the user navigated (Back / Change answer)
       * instead of answering: clears pendingPrompt but leaves the prompt
       * segment unanswered - the re-ask arrives as a new awaiting_input.
       */
      navigation?: 'back' | 'revise';
    };

// ─────────────────────────────────────────────────────────────────────────────
// Slice helpers - project/restore the per-session fields
// ─────────────────────────────────────────────────────────────────────────────

/** Extract the per-session slice from the top-level ChatState. */
function extractSlice(state: ChatState): SessionSlice {
  return {
    connectionStatus: state.connectionStatus,
    reconnectAttempt: state.reconnectAttempt,
    conversationId: state.conversationId,
    turns: state.turns,
    historyStatus: state.historyStatus,
    activeRunId: state.activeRunId,
    hasMoreHistory: state.hasMoreHistory,
    oldestMessageIndex: state.oldestMessageIndex,
    lastSeq: state.lastSeq,
    lastRunSeq: state.lastRunSeq,
    lastRunSeqRunId: state.lastRunSeqRunId,
    hasUnread: false, // active session is always considered read
  };
}

/** Merge an updated session slice back into the top-level ChatState. */
function applySliceToState(state: ChatState, slice: SessionSlice): ChatState {
  return {
    ...state,
    connectionStatus: slice.connectionStatus,
    reconnectAttempt: slice.reconnectAttempt,
    conversationId: slice.conversationId,
    turns: slice.turns,
    historyStatus: slice.historyStatus,
    activeRunId: slice.activeRunId,
    hasMoreHistory: slice.hasMoreHistory,
    oldestMessageIndex: slice.oldestMessageIndex,
    lastSeq: slice.lastSeq,
    lastRunSeq: slice.lastRunSeq,
    lastRunSeqRunId: slice.lastRunSeqRunId,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function findActiveRun(slice: SessionSlice): AssistantRun | null {
  if (!slice.activeRunId) return null;
  for (let i = slice.turns.length - 1; i >= 0; i--) {
    const turn = slice.turns[i];
    if (turn?.kind === 'assistant' && turn.runId === slice.activeRunId) {
      return turn;
    }
  }
  return null;
}

/**
 * Immutably update the active assistant run in a session slice.
 */
function updateActiveRun(
  slice: SessionSlice,
  updater: (run: AssistantRun) => AssistantRun
): SessionSlice {
  const run = findActiveRun(slice);
  if (!run) return slice;
  return {
    ...slice,
    turns: slice.turns.map((t) =>
      t.kind === 'assistant' && t.runId === run.runId ? updater(t) : t
    ),
  };
}

// ── Segment helpers (conversational transcript projection) ───────────────────

/**
 * Append a text segment for a new messageId (idempotent for replays). `order` is the creating
 * event's per-conversation seq, used by the view to interleave this narration with the step chain.
 */
function withTextSegmentStarted(run: AssistantRun, messageId: string, order: number): RunSegment[] {
  const segments = run.segments ?? [];
  if (segments.some((s) => s.kind === 'text' && s.messageId === messageId)) return segments;
  return [...segments, { kind: 'text', messageId, text: '', order }];
}

/** Append a delta to the matching text segment (creating one if needed; `order` stamps a new one). */
function withTextDelta(
  run: AssistantRun,
  messageId: string,
  delta: string,
  order: number
): RunSegment[] {
  const segments = run.segments ?? [];
  const idx = segments.findIndex((s) => s.kind === 'text' && s.messageId === messageId);
  if (idx === -1) {
    return [...segments, { kind: 'text', messageId, text: delta, order }];
  }
  return segments.map((s, i) =>
    i === idx && s.kind === 'text' ? { ...s, text: s.text + delta } : s
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────────────────────────────────────

export function chatReducer(state: ChatState, action: ChatActionType): ChatState {
  switch (action.type) {
    case 'RESET':
      return { ...initialChatState, selectedContext: state.selectedContext };

    case 'CONNECTION_STATUS': {
      if (action.sessionUuid === state.activeSessionUuid) {
        return { ...state, connectionStatus: action.status };
      }
      const existing = state.sessionPool[action.sessionUuid] ?? initialSessionSlice;
      return {
        ...state,
        sessionPool: {
          ...state.sessionPool,
          [action.sessionUuid]: { ...existing, connectionStatus: action.status },
        },
      };
    }

    case 'RECONNECT_ATTEMPT': {
      if (action.sessionUuid === state.activeSessionUuid) {
        return { ...state, reconnectAttempt: action.attempt };
      }
      const existing = state.sessionPool[action.sessionUuid] ?? initialSessionSlice;
      return {
        ...state,
        sessionPool: {
          ...state.sessionPool,
          [action.sessionUuid]: { ...existing, reconnectAttempt: action.attempt },
        },
      };
    }

    case 'SET_ACTION_RESULT': {
      const updatedTurns = state.turns.map((turn) => {
        if (turn.kind !== 'assistant' || turn.runId !== action.runId) return turn;
        if (action.cardKey) {
          return {
            ...turn,
            cardActionResults: {
              ...turn.cardActionResults,
              [action.cardKey]: {
                result: action.actionResult,
                ...(action.acceptedFieldKeys ? { fieldKeys: action.acceptedFieldKeys } : {}),
              },
            },
          };
        }
        return {
          ...turn,
          actionResult: action.actionResult,
          acceptedFieldKeys: action.acceptedFieldKeys,
        };
      });
      return { ...state, turns: updatedTurns };
    }

    case 'PROMPT_ANSWERED': {
      const updatedTurns = state.turns.map((turn) => {
        if (turn.kind !== 'assistant' || turn.runId !== action.runId) return turn;
        return {
          ...turn,
          lifecycleState: 'resuming' as const,
          pendingPrompt: undefined,
          // Navigation (Back / Change answer) is not an answer - the question
          // stays unanswered; the skill re-asks via a fresh awaiting_input.
          segments: action.navigation
            ? turn.segments
            : turn.segments?.map((s) =>
                s.kind === 'prompt' && s.promptId === action.promptId
                  ? {
                      ...s,
                      answer: action.answer,
                      formAnswers: action.formAnswers,
                      answerAssetUuid: action.assetUuid,
                      answerAssetUuids: action.assetUuids,
                      answerPreviews: action.answerPreviews,
                    }
                  : s
              ),
        };
      });
      return { ...state, turns: updatedTurns };
    }

    case 'SET_CONTEXT':
      return { ...state, selectedContext: { ...state.selectedContext, ...action.context } };

    case 'SET_COMPOSER_MODE':
      return { ...state, composerMode: action.mode };

    case 'USER_TURN_SENT': {
      const userTurn: UserTurn = {
        kind: 'user',
        id: action.runId + '-user',
        prompt: action.prompt,
        context: action.context,
        ts: new Date().toISOString(),
        // Button-driven runs carry their source so the pill renders immediately
        // (history rehydration restores it from chat_message.trigger_source).
        ...(action.triggerSource && action.triggerSource !== 'user_chat'
          ? { triggerSource: action.triggerSource }
          : {}),
        // Precise origin (page + exact button label) for the source pill.
        ...(action.origin ? { origin: action.origin } : {}),
      };
      return { ...state, turns: [...state.turns, userTurn] };
    }

    case 'USER_TURN_ABORTED': {
      // Remove the optimistic user bubble that was added by USER_TURN_SENT
      // for a run that never reached the server. Also remove the optimistic
      // AssistantRun if OPTIMISTIC_RUN_PENDING was dispatched beforehand.
      const abortedId = action.runId + '-user';
      return {
        ...state,
        turns: state.turns.filter(
          (t) =>
            (t.kind !== 'user' || t.id !== abortedId) &&
            (t.kind !== 'assistant' || t.runId !== action.runId)
        ),
      };
    }

    case 'OPTIMISTIC_RUN_PENDING': {
      // Immediately add an AssistantRun placeholder so the agent bubble appears
      // before beforeSend resolves. Does NOT set activeRunId - that waits for
      // the real run_started from the server, which will update model/mode.
      const newRun: AssistantRun = {
        kind: 'assistant',
        runId: action.runId,
        messageId: null,
        lifecycleState: 'pending_start',
        model: action.model,
        mode: action.mode,
        text: '',
        streaming: false,
        childEntries: [],
        ragReferences: [],
        executionStatus: createInitialExecutionStatus(new Date().toISOString()),
        startedAt: new Date().toISOString(),
        initiator: 'user',
      };
      return { ...state, turns: [...state.turns, newRun] };
    }

    case 'SERVER_EVENT': {
      const isActive = action.sessionUuid === state.activeSessionUuid;
      if (isActive) {
        return applySliceToState(state, applyServerEvent(extractSlice(state), action.event));
      }
      const existing = state.sessionPool[action.sessionUuid] ?? initialSessionSlice;
      const updatedSlice = applyServerEvent(existing, action.event);
      // Mark background session as having an unread response when a run finishes.
      const isRunEnd =
        action.event.type === 'run_completed' ||
        action.event.type === 'run_error' ||
        action.event.type === 'run_cancelled';
      return {
        ...state,
        sessionPool: {
          ...state.sessionPool,
          [action.sessionUuid]: isRunEnd ? { ...updatedSlice, hasUnread: true } : updatedSlice,
        },
      };
    }

    case 'HISTORY_STATUS': {
      if (action.sessionUuid === state.activeSessionUuid) {
        return { ...state, historyStatus: action.status };
      }
      const existing = state.sessionPool[action.sessionUuid] ?? initialSessionSlice;
      return {
        ...state,
        sessionPool: {
          ...state.sessionPool,
          [action.sessionUuid]: { ...existing, historyStatus: action.status },
        },
      };
    }

    case 'HISTORY_LOADED':
      // Prepend historical turns before any turns from the current WS session.
      // If turns already exist (e.g. race with a very fast first message), keep
      // current turns appended after history.
      return {
        ...state,
        historyStatus: 'loaded',
        turns: [...action.turns, ...state.turns],
      };

    case 'SESSIONS_STATUS':
      return { ...state, sessionsStatus: action.status };

    case 'SESSIONS_LOADED': {
      // Preserve the active session if it isn't in the loaded list.
      // This prevents a race condition where the REST load completes after
      // SESSION_CREATED (e.g. DB SELECT ran before INSERT committed), which
      // would cause the newly-created session to vanish from the dropdown.
      let sessions = action.sessions;
      if (state.activeSessionUuid) {
        const isPresent = sessions.some((s) => s.uuid === state.activeSessionUuid);
        if (!isPresent) {
          const active = state.sessions.find((s) => s.uuid === state.activeSessionUuid);
          if (active) sessions = [active, ...sessions];
        }
      }
      return { ...state, sessionsStatus: 'loaded', sessions };
    }

    case 'SESSION_TITLE_UPDATED': {
      return {
        ...state,
        sessions: state.sessions.map((s) =>
          s.uuid === action.sessionUuid ? { ...s, title: action.title } : s
        ),
      };
    }

    case 'SESSION_CREATED': {
      const fromUuid = state.activeSessionUuid;
      // Save departing session's slice to the pool before switching.
      const updatedPool = fromUuid
        ? { ...state.sessionPool, [fromUuid]: extractSlice(state) }
        : state.sessionPool;
      return {
        ...state,
        sessions: [action.session, ...state.sessions],
        activeSessionUuid: action.session.uuid,
        composerMode: asChatMode(action.session.mode) ?? state.composerMode,
        // skipClearTurns=true is used by sendRun's auto-create so the user's
        // in-flight message is not wiped before the response arrives.
        turns: action.skipClearTurns ? state.turns : [],
        historyStatus: 'loaded',
        activeRunId: action.skipClearTurns ? state.activeRunId : null,
        conversationId: null,
        connectionStatus: 'idle',
        lastSeq: 0,
        hasMoreHistory: false,
        oldestMessageIndex: null,
        sessionPool: updatedPool,
      };
    }

    case 'SESSION_SWITCH_STARTED': {
      const fromUuid = state.activeSessionUuid;
      const toUuid = action.sessionUuid;

      // Save the departing session's full slice (including actual activeRunId) so
      // that its dedicated WS connection can keep updating it in the background.
      const updatedPool = fromUuid
        ? { ...state.sessionPool, [fromUuid]: extractSlice(state) }
        : state.sessionPool;

      // Restore from pool if we have prior state for the target session
      // (e.g. switching back to a session with an in-flight or completed run).
      const targetMode = asChatMode(state.sessions.find((s2) => s2.uuid === toUuid)?.mode);

      const poolEntry = updatedPool[toUuid];
      if (poolEntry) {
        // Remove from pool - it becomes the live top-level state.
        // Also clear hasUnread since the user is now viewing this session.
        const { [toUuid]: _restored, ...remainingPool } = updatedPool;
        return applySliceToState(
          {
            ...state,
            activeSessionUuid: toUuid,
            sessionPool: remainingPool,
            composerMode: targetMode ?? state.composerMode,
          },
          { ...poolEntry, hasUnread: false }
        );
      }

      // No pool entry - fresh session, blank slate.
      return {
        ...state,
        activeSessionUuid: toUuid,
        composerMode: targetMode ?? state.composerMode,
        sessionPool: updatedPool,
        turns: [],
        historyStatus: 'loading',
        activeRunId: null,
        conversationId: null,
        connectionStatus: 'idle',
        lastSeq: 0,
        hasMoreHistory: false,
        oldestMessageIndex: null,
      };
    }

    case 'SESSION_SWITCHED': {
      const isActive = action.sessionUuid === state.activeSessionUuid;

      /**
       * Merge DB history with any in-flight run turns.
       *
       * When switching back to a session mid-run, SESSION_SWITCH_STARTED
       * already restored the pool slice (which contains the live in-flight
       * turns). But the history fetch returns DB state - which won't include
       * the in-progress assistant turn (not persisted yet).
       *
       * If the target session still has an activeRunId, append the in-flight
       * user+assistant pair to the DB turns so the run remains visible.
       */
      const mergeTurns = (
        liveTurns: ConversationTurn[],
        activeRunId: string | null
      ): ConversationTurn[] => {
        if (!activeRunId) {
          // Preserve a recently-cancelled or errored turn that may not yet be committed to
          // DB. Race: the post-run history fetch (triggered by activeRunId →
          // null) can resolve before persistRunMessages completes on the server;
          // without this guard the optimistic pair would be dropped and
          // messages would briefly disappear from the UI.
          const lastLive = liveTurns[liveTurns.length - 1];
          // Preserve an optimistic user turn added before run_started: the WS
          // event has not arrived yet so activeRunId is still null, but the
          // user message should stay visible while the run is starting.
          if (
            lastLive?.kind === 'user' &&
            !action.turns.some((t) => t.kind === 'user' && t.id === lastLive.id)
          ) {
            return [...action.turns, lastLive];
          }
          if (
            lastLive?.kind === 'assistant' &&
            (lastLive.lifecycleState === 'cancelled' || lastLive.lifecycleState === 'error') &&
            !action.turns.some((t) => t.kind === 'assistant' && t.runId === lastLive.runId)
          ) {
            const runIdx = liveTurns.findIndex(
              (t) => t.kind === 'assistant' && t.runId === lastLive.runId
            );
            const prevTurn = runIdx > 0 ? liveTurns[runIdx - 1] : undefined;
            const pending: ConversationTurn[] =
              prevTurn?.kind === 'user' ? [prevTurn, lastLive] : [lastLive];
            return [...action.turns, ...pending];
          }
          return action.turns;
        }
        // Bail if DB already has this run (e.g. it completed and was persisted)
        if (action.turns.some((t) => t.kind === 'assistant' && t.runId === activeRunId)) {
          return action.turns;
        }
        // Find the in-flight run and its preceding user turn in live state
        const runIdx = liveTurns.findIndex(
          (t) => t.kind === 'assistant' && t.runId === activeRunId
        );
        if (runIdx === -1) return action.turns;
        const inFlight: ConversationTurn[] =
          runIdx > 0 && liveTurns[runIdx - 1]?.kind === 'user'
            ? [liveTurns[runIdx - 1]!, liveTurns[runIdx]!]
            : [liveTurns[runIdx]!];
        return [...action.turns, ...inFlight];
      };

      const historyUpdate = {
        historyStatus: 'loaded' as const,
        hasMoreHistory: action.hasMore,
        oldestMessageIndex: action.oldestMessageIndex,
      };

      if (isActive) {
        // Remove from pool (it's now live at the top level) and update top-level.
        const { [action.sessionUuid]: _removed, ...remainingPool } = state.sessionPool;
        return {
          ...state,
          ...historyUpdate,
          turns: mergeTurns(state.turns, state.activeRunId),
          sessionPool: remainingPool,
        };
      }
      // Session is in the background - update its pool entry with the confirmed DB state.
      const existing = state.sessionPool[action.sessionUuid] ?? initialSessionSlice;
      return {
        ...state,
        sessionPool: {
          ...state.sessionPool,
          [action.sessionUuid]: {
            ...existing,
            ...historyUpdate,
            turns: mergeTurns(existing.turns, existing.activeRunId),
          },
        },
      };
    }

    case 'OLDER_HISTORY_LOADED':
      return {
        ...state,
        historyStatus: 'loaded',
        // Prepend older turns before the existing ones
        turns: [...action.turns, ...state.turns],
        hasMoreHistory: action.hasMore,
        oldestMessageIndex: action.oldestMessageIndex,
      };

    // ── Explicit context ───────────────────────────────────────────────────

    case 'ADD_EXPLICIT_CONTEXT': {
      // Guard: ignore duplicate ids (idempotent re-clicks)
      if (state.explicitContextItems.some((i) => i.id === action.item.id)) {
        return state;
      }
      return {
        ...state,
        explicitContextItems: [...state.explicitContextItems, action.item],
      };
    }

    case 'REMOVE_EXPLICIT_CONTEXT':
      return {
        ...state,
        explicitContextItems: state.explicitContextItems.filter((i) => i.id !== action.itemId),
      };

    case 'CLEAR_EXPLICIT_CONTEXT':
      return { ...state, explicitContextItems: [], draftExplicitItem: null };

    case 'SET_DRAFT_EXPLICIT_CONTEXT':
      // Replace any existing draft - only one draft at a time.
      return { ...state, draftExplicitItem: action.item };

    case 'CONFIRM_DRAFT_EXPLICIT_CONTEXT': {
      const draft = state.draftExplicitItem;
      if (!draft) return state;
      // Avoid duplicates: if the same item (by id) is somehow already confirmed, skip.
      const alreadyConfirmed = state.explicitContextItems.some((i) => i.id === draft.id);
      return {
        ...state,
        explicitContextItems: alreadyConfirmed
          ? state.explicitContextItems
          : [...state.explicitContextItems, draft],
        draftExplicitItem: null,
      };
    }

    case 'CLEAR_DRAFT_EXPLICIT_CONTEXT':
      return { ...state, draftExplicitItem: null };

    case 'CANCEL_RUN_OPTIMISTIC': {
      // Immediately clear activeRunId and mark the matching run as cancelled
      // without waiting for the server's run_cancelled ack. The server ack will
      // arrive later as a near-no-op (updateActiveRun is a no-op when activeRunId is null).
      const updatedTurns = state.turns.map((t) =>
        t.kind === 'assistant' && t.runId === action.runId
          ? ({
              ...t,
              lifecycleState: 'cancelled' as const,
              streaming: false,
              endedAt: action.ts,
              pendingPrompt: undefined,
              executionStatus: t.executionStatus
                ? cancelExecution(t.executionStatus, action.ts)
                : undefined,
            } satisfies AssistantRun)
          : t
      );
      return { ...state, turns: updatedTurns, activeRunId: null };
    }

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Event application
// ─────────────────────────────────────────────────────────────────────────────

function applyServerEvent(slice: SessionSlice, event: ServerEvent): SessionSlice {
  // Detect and warn on sequence gaps (non-destructive)
  if ('seq' in event && event.seq > slice.lastSeq + 1 && slice.lastSeq > 0) {
    console.warn(`[chatReducer] Sequence gap: expected ${slice.lastSeq + 1}, got ${event.seq}`);
  }

  // [Phase 3] Per-run dedup: events carry a monotonic runSeq. After a reconnect,
  // subscribe_run replays persisted events; drop any we've already applied for the
  // SAME run. runSeq resets per run, so only dedup when the runId matches the tracked
  // one (a new run's runSeq=1 must not be dropped against a previous run's high seq).
  const evRunSeq = (event as { runSeq?: number }).runSeq;
  const evRunId = (event as { runId?: string }).runId;
  const sameTrackedRun = typeof evRunId === 'string' && evRunId === slice.lastRunSeqRunId;
  if (typeof evRunSeq === 'number' && sameTrackedRun && evRunSeq <= slice.lastRunSeq) {
    return slice; // duplicate (replayed or re-fanned) - already applied
  }

  const withSeq: SessionSlice = {
    ...slice,
    lastSeq: Math.max(slice.lastSeq, 'seq' in event ? event.seq : slice.lastSeq),
    ...(typeof evRunSeq === 'number' && typeof evRunId === 'string'
      ? {
          lastRunSeqRunId: evRunId,
          lastRunSeq: sameTrackedRun ? Math.max(slice.lastRunSeq, evRunSeq) : evRunSeq,
        }
      : {}),
  };

  switch (event.type) {
    case 'session_connected':
      return {
        ...withSeq,
        connectionStatus: 'connected',
        conversationId: event.conversationId,
      };

    case 'run_started': {
      // If OPTIMISTIC_RUN_PENDING already created a placeholder for this runId,
      // just set activeRunId and update model/mode with the real server values.
      const existingIdx = withSeq.turns.findIndex(
        (t) => t.kind === 'assistant' && t.runId === event.runId
      );
      if (existingIdx !== -1) {
        return {
          ...withSeq,
          activeRunId: event.runId,
          turns: withSeq.turns.map((t, i) =>
            i === existingIdx && t.kind === 'assistant'
              ? {
                  ...t,
                  model: event.model || t.model,
                  mode: event.mode || t.mode,
                  initiator: event.initiator ?? t.initiator,
                  // Transition from 'queued' to 'initializing' so the status bar
                  // shows a live spinner as soon as the real run_started arrives.
                  executionStatus: t.executionStatus
                    ? { ...t.executionStatus, phase: 'initializing' as const }
                    : t.executionStatus,
                }
              : t
          ),
        };
      }
      const newRun: AssistantRun = {
        kind: 'assistant',
        runId: event.runId,
        messageId: null,
        lifecycleState: 'pending_start',
        model: event.model,
        mode: event.mode,
        text: '',
        streaming: false,
        childEntries: [],
        ragReferences: [],
        executionStatus: createInitialExecutionStatus(event.ts),
        startedAt: event.ts,
        initiator: event.initiator ?? 'user',
      };
      return {
        ...withSeq,
        activeRunId: event.runId,
        turns: [...withSeq.turns, newRun],
      };
    }

    case 'status': {
      const step = createProgressStep(event);
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        childEntries: [
          ...run.childEntries,
          {
            kind: 'status' as const,
            id: `status-${withSeq.lastSeq}`,
            label: event.label,
            ts: event.ts,
          },
        ],
        executionStatus: run.executionStatus ? addStep(run.executionStatus, step) : undefined,
      }));
    }

    case 'agent_step_started': {
      const step = createAgentStep(event);
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus ? addStep(run.executionStatus, step) : undefined,
      }));
    }

    case 'agent_step_completed': {
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus
          ? updateStep(run.executionStatus, event.stepId, (step) => completeAgentStep(step, event))
          : undefined,
      }));
    }

    case 'agent_step_failed': {
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus
          ? updateStep(run.executionStatus, event.stepId, (step) => failAgentStep(step, event))
          : undefined,
      }));
    }

    case 'plan_created': {
      const planSteps = createPlanStepsFromExecutionPlan(event);
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus
          ? { ...run.executionStatus, steps: [...planSteps, ...run.executionStatus.steps] }
          : { phase: 'planning' as const, steps: planSteps, startedAt: event.ts },
      }));
    }

    case 'plan_step_started': {
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus
          ? updateStep(run.executionStatus, event.stepId, (step) => ({
              ...step,
              status: 'running' as const,
              startedAt: event.ts,
              // Restamp order to this step's start seq so it moves from the queued "upcoming"
              // preview (PENDING_PLAN_STEP_ORDER_BASE) to its execution point, interleaving inline
              // with the phase narration instead of the whole plan bolting above the segments.
              order: event.seq,
            }))
          : undefined,
      }));
    }

    case 'plan_step_completed': {
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus
          ? updateStep(run.executionStatus, event.stepId, (step) => ({
              ...step,
              status: 'completed' as const,
              completedAt: event.ts,
            }))
          : undefined,
      }));
    }

    case 'plan_step_failed': {
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus
          ? updateStep(run.executionStatus, event.stepId, (step) => ({
              ...step,
              status: 'failed' as const,
              error: event.error,
              completedAt: event.ts,
            }))
          : undefined,
      }));
    }

    case 'plan_step_skipped': {
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        executionStatus: run.executionStatus
          ? updateStep(run.executionStatus, event.stepId, (step) => ({
              ...step,
              status: 'skipped' as const,
              completedAt: event.ts,
            }))
          : undefined,
      }));
    }

    case 'message_started': {
      // We deliberately do NOT add a "Generating response" trace step here.
      // Agentic/conversational skills stream preamble text before deciding to
      // call tools, so a step created on the first token lands mid-trace (ahead
      // of the tool steps) and is redundant with the answer that streams right
      // below. Non-agentic single-turn skills get their correctly-placed step
      // from the explicit agent_step_started('step-generate') lifecycle instead.
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        messageId: event.messageId,
        lifecycleState: 'streaming',
        streaming: true,
        segments: withTextSegmentStarted(run, event.messageId, event.seq),
        executionStatus: run.executionStatus,
      }));
    }

    case 'message_delta':
      return updateActiveRun(withSeq, (run) => {
        // First visible token: flip the running generation step from "Thinking"
        // (compute window) to "Responding". Match on the label, not the
        // 'step-generate' id, so it survives sub-agent id namespacing.
        const isFirstToken = run.text === '' && event.delta.length > 0;
        const activeStep = run.executionStatus?.activeStepId
          ? run.executionStatus.steps.find((s) => s.id === run.executionStatus!.activeStepId)
          : undefined;
        const executionStatus =
          isFirstToken &&
          run.executionStatus &&
          activeStep?.status === 'running' &&
          activeStep.label === GENERATION_THINKING_LABEL
            ? updateStep(run.executionStatus, activeStep.id, (step) => ({
                ...step,
                label: GENERATION_RESPONDING_LABEL,
              }))
            : run.executionStatus;
        return {
          ...run,
          text: run.text + event.delta,
          segments: withTextDelta(run, event.messageId, event.delta, event.seq),
          executionStatus,
        };
      });

    case 'message_completed': {
      return updateActiveRun(withSeq, (run) => {
        const responseStepId = run.messageId ? `step-response-${run.messageId}` : undefined;
        const updatedStatus =
          run.executionStatus && responseStepId
            ? updateStep(run.executionStatus, responseStepId, (step) =>
                completeResponseStep(step as any, event.ts)
              )
            : run.executionStatus;

        return {
          ...run,
          text: event.text,
          streaming: false,
          lifecycleState: 'finalizing',
          executionStatus: updatedStatus,
        };
      });
    }

    case 'rag_references': {
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        ragReferences: [...run.ragReferences, ...event.references],
        executionStatus: run.executionStatus
          ? (() => {
              const lastRetrieveStep = [...run.executionStatus.steps]
                .reverse()
                .find((s) => s.type === 'retrieve_context');
              if (lastRetrieveStep) {
                const citedCount = event.references.filter((r) => r.cited).length;
                return updateStep(run.executionStatus, lastRetrieveStep.id, (step) => ({
                  ...step,
                  sourceCount: event.references.length,
                  citedCount,
                }));
              }
              const step = createContextRetrievalStep(event);
              return addStep(run.executionStatus, step);
            })()
          : undefined,
      }));
    }

    case 'tool_started': {
      const { parentStepId, ts } = event;

      if (parentStepId) {
        // Nested: append ToolCallRecord to matching parent step's toolCalls[]
        return updateActiveRun(withSeq, (run) => ({
          ...run,
          executionStatus: run.executionStatus
            ? updateStep(run.executionStatus, parentStepId, (step) => {
                const s = step as GenericProgressStep | ContextRetrievalStep;
                return {
                  ...s,
                  toolCalls: [
                    ...(s.toolCalls ?? []),
                    {
                      toolCallId: event.toolCallId,
                      toolName: event.toolName,
                      toolLabel: event.label ?? event.toolName,
                      inputSummary: event.inputSummary ?? '',
                      status: 'running' as const,
                      startedAt: ts,
                    },
                  ],
                };
              })
            : undefined,
        }));
      }

      // Standalone: add flat ToolExecutionStep (current behavior, backward compat)
      const step = createToolExecutionStep(event);
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        childEntries: [
          ...run.childEntries,
          {
            kind: 'tool' as const,
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            label: event.label,
            status: 'running' as const,
            ts: event.ts,
          },
        ],
        // Conversational transcript: standalone tools appear inline between text
        // segments (nested parentStepId tools stay drawer-only). ask_user is skipped -
        // it surfaces as a prompt segment via awaiting_input instead.
        segments:
          event.toolName === 'ask_user'
            ? run.segments
            : [
                ...(run.segments ?? []),
                {
                  kind: 'tool' as const,
                  toolCallId: event.toolCallId,
                  toolName: event.toolName,
                  label: event.label,
                  status: 'running' as const,
                  order: event.seq,
                },
              ],
        executionStatus: run.executionStatus ? addStep(run.executionStatus, step) : undefined,
      }));
    }

    case 'tool_delta': {
      return updateActiveRun(withSeq, (run) => {
        // Try to find parent step with nested tool call first
        if (run.executionStatus) {
          for (const step of run.executionStatus.steps) {
            const s = step as GenericProgressStep | ContextRetrievalStep;
            if (s.toolCalls) {
              const toolCall = s.toolCalls.find((tc) => tc.toolCallId === event.toolCallId);
              if (toolCall) {
                // Update nested tool call
                return {
                  ...run,
                  executionStatus: updateStep(run.executionStatus, step.id, (parentStep) => {
                    const ps = parentStep as GenericProgressStep | ContextRetrievalStep;
                    return {
                      ...ps,
                      toolCalls: ps.toolCalls!.map((tc) =>
                        tc.toolCallId === event.toolCallId
                          ? { ...tc, progressText: (tc.progressText ?? '') + event.delta }
                          : tc
                      ),
                    };
                  }),
                };
              }
            }
          }
        }

        // Fallback: update flat tool execution step
        const stepId = `step-tool-${event.toolCallId}`;
        const updatedStatus = run.executionStatus
          ? updateStep(run.executionStatus, stepId, (step) =>
              updateToolProgress(step as any, event)
            )
          : undefined;

        return {
          ...run,
          childEntries: run.childEntries.map((entry) =>
            entry.kind === 'tool' && entry.toolCallId === event.toolCallId
              ? { ...entry, progressText: (entry.progressText ?? '') + event.delta }
              : entry
          ),
          executionStatus: updatedStatus,
        };
      });
    }

    case 'tool_completed': {
      return updateActiveRun(withSeq, (run) => {
        // Try to find parent step with nested tool call first
        if (run.executionStatus) {
          for (const step of run.executionStatus.steps) {
            const s = step as GenericProgressStep | ContextRetrievalStep;
            if (s.toolCalls) {
              const toolCall = s.toolCalls.find((tc) => tc.toolCallId === event.toolCallId);
              if (toolCall) {
                // Update nested tool call
                return {
                  ...run,
                  executionStatus: updateStep(run.executionStatus, step.id, (parentStep) => {
                    const ps = parentStep as GenericProgressStep | ContextRetrievalStep;
                    return {
                      ...ps,
                      toolCalls: ps.toolCalls!.map((tc) =>
                        tc.toolCallId === event.toolCallId
                          ? {
                              ...tc,
                              status: event.success ? ('completed' as const) : ('failed' as const),
                              resultSummary: event.resultSummary,
                              durationMs: event.durationMs,
                              completedAt: event.ts,
                            }
                          : tc
                      ),
                    };
                  }),
                };
              }
            }
          }
        }

        // Fallback: update flat tool execution step
        const stepId = `step-tool-${event.toolCallId}`;
        const updatedStatus = run.executionStatus
          ? updateStep(run.executionStatus, stepId, (step) =>
              completeToolStep(step as any, event, event.ts)
            )
          : undefined;

        return {
          ...run,
          childEntries: run.childEntries.map((entry) =>
            entry.kind === 'tool' && entry.toolCallId === event.toolCallId
              ? {
                  ...entry,
                  status: event.success ? ('completed' as const) : ('failed' as const),
                  resultSummary: event.resultSummary,
                }
              : entry
          ),
          segments: run.segments?.map((s) =>
            s.kind === 'tool' && s.toolCallId === event.toolCallId
              ? {
                  ...s,
                  status: event.success ? ('completed' as const) : ('failed' as const),
                  resultSummary: event.resultSummary,
                }
              : s
          ),
          executionStatus: updatedStatus,
        };
      });
    }

    case 'awaiting_confirmation':
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        lifecycleState: 'awaiting_confirmation',
        confirmationPrompt: event.prompt,
        confirmationDetails: event.details,
      }));

    case 'awaiting_input':
      // [Phase 4D] Generalised mid-run prompt (confirm/choice/text). Reuses the
      // awaiting_confirmation lifecycle; richer prompt data lives on pendingPrompt.
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        lifecycleState: 'awaiting_confirmation',
        pendingPrompt: {
          promptId: event.promptId,
          kind: event.kind,
          prompt: event.prompt,
          details: event.details,
          choices: event.choices,
          recommendedChoiceId: event.recommendedChoiceId,
          allowFreeText: event.allowFreeText,
          closedChoices: event.closedChoices,
          multiSelect: event.multiSelect,
          minSelections: event.minSelections,
          maxSelections: event.maxSelections,
          suggestions: event.suggestions,
          file: event.file,
          link: event.link,
          questions: event.questions,
          canGoBack: event.canGoBack,
        },
        // Conversational transcript: the question appears inline, in order
        // (idempotent on promptId for replays).
        segments: run.segments?.some((s) => s.kind === 'prompt' && s.promptId === event.promptId)
          ? run.segments
          : [
              ...(run.segments ?? []),
              {
                kind: 'prompt' as const,
                promptId: event.promptId,
                promptKind: event.kind,
                prompt: event.prompt,
                details: event.details,
                choices: event.choices,
                allowFreeText: event.allowFreeText,
                order: event.seq,
              },
            ],
      }));

    case 'run_paused':
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        lifecycleState: 'paused',
      }));

    case 'run_resumed':
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        lifecycleState: 'resuming',
      }));

    case 'run_completed':
      return {
        ...updateActiveRun(withSeq, (run) => ({
          ...run,
          messageId: event.messageUuid ?? run.messageId,
          lifecycleState: 'completed',
          streaming: false,
          endedAt: event.ts,
          tokenUsage: event.usage,
          executionStatus: run.executionStatus
            ? completeExecution(run.executionStatus, event.ts)
            : undefined,
        })),
        activeRunId: null,
      };

    case 'run_cancelled':
      return {
        ...updateActiveRun(withSeq, (run) => ({
          ...run,
          lifecycleState: 'cancelled',
          streaming: false,
          endedAt: event.ts,
          pendingPrompt: undefined,
          executionStatus: run.executionStatus
            ? cancelExecution(run.executionStatus, event.ts)
            : undefined,
        })),
        activeRunId: null,
      };

    case 'run_error':
      return {
        ...updateActiveRun(withSeq, (run) => ({
          ...run,
          lifecycleState: 'error',
          streaming: false,
          errorCode: event.code,
          errorMessage: event.message,
          retryable: event.retryable ?? true,
          endedAt: event.ts,
          executionStatus: run.executionStatus
            ? failExecution(run.executionStatus, event.message, event.ts)
            : undefined,
        })),
        activeRunId: null,
      };

    case 'run_replay_complete': {
      // [reconnect] After re-subscribing post-reconnect, the server reports whether the run
      // is still executing. If it is gone (stillRunning === false) while we are still showing
      // its ask_user prompt card, the run cannot be resumed - most often the API process
      // restarted (e.g. `tsx watch` in local dev) and wiped the in-memory run plus its parked
      // prompt resolver. Leaving the card up would present dead controls whose clicks get
      // silently dropped server-side, so replace it with the standard retryable error.
      if (event.stillRunning) return withSeq;
      const replayRun = findActiveRun(withSeq);
      if (!replayRun || replayRun.runId !== event.runId || !replayRun.pendingPrompt) {
        return withSeq;
      }
      return {
        ...updateActiveRun(withSeq, (run) => ({
          ...run,
          lifecycleState: 'error' as const,
          streaming: false,
          pendingPrompt: undefined,
          errorCode: 'run_not_active',
          errorMessage:
            'This run ended because the connection to the server was lost. Run the command again to continue.',
          retryable: true,
          endedAt: event.ts,
          executionStatus: run.executionStatus
            ? failExecution(run.executionStatus, 'Connection to the server was lost', event.ts)
            : undefined,
        })),
        activeRunId: null,
      };
    }

    case 'pong':
      // Heartbeat - no state change
      return withSeq;

    case 'structured_payload': {
      // Validate the payload at the boundary before storing
      const parsed = parseAiStructuredPayload(event.payload);
      if (!parsed) {
        console.warn('[chatReducer] Received malformed structured_payload event - ignored', event);
        return withSeq;
      }
      // Look up the run by runId rather than activeRunId - the structured_payload
      // event can arrive after reconcileStalledRun has already cleared activeRunId
      // (e.g. when the poll fires after artifact writes but before the WS post-run
      // code sends the payload).
      const targetRunId = (event as unknown as { runId?: string }).runId;
      if (targetRunId) {
        const updatedTurns = withSeq.turns.map((t) =>
          t.kind === 'assistant' && t.runId === targetRunId
            ? { ...t, structuredPayload: parsed }
            : t
        );
        return { ...withSeq, turns: updatedTurns };
      }
      // Fallback: no runId on event - update the active run
      return updateActiveRun(withSeq, (run) => ({
        ...run,
        structuredPayload: parsed,
      }));
    }

    default:
      return withSeq;
  }
}
